import { ApiTotalsModel, ReviewQueryModel } from '@loavies/c2c-models'
import { ErrorHandlerService } from '@loavies/utils'

import { mapRelations } from '../response-mappers/relations-mapper'
import { mapReviews } from '../response-mappers/review-mappers'
import { PAGINATION } from './constants/pagination'

export class MarketplaceReviewsService {
  constructor(marketplaceService) {
    this.marketplaceService = marketplaceService
    this.marketplace = marketplaceService.instance
    this.errorHandlerService = new ErrorHandlerService()
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-reviews
   * @param {string} id userId
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  reviews: loavies.models.review.ReviewModel[],
   *  totals: loavies.models.api.ApiTotalsModel
   * }>}
   */
  async getReviewsAboutUserId({ id, page, perPage }) {
    const query = new ReviewQueryModel({ subjectId: id })

    return this.query({ query, page, perPage })
      .then(({ data, meta }) => this.mapMultipleReviewsResponse(data, meta))
  }

  /**
   * @param {Array} data
   * @param {Object} meta
   * @return {{
   *  reviews: loavies.models.review.ReviewModel[],
   *  totals: loavies.models.api.ApiTotalsModel
   * }}
   */
  mapMultipleReviewsResponse(data, meta) {
    return {
      reviews: mapReviews(data),
      totals: new ApiTotalsModel(meta),
    }
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-reviews
   * @param query {loavies.models.review.ReviewQueryModel}
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  data: Array,
   *  meta: Object,
   * }>}
   */
  async query({
    query = new ReviewQueryModel({}),
    page = PAGINATION.DEFAULT_PAGE,
    perPage = PAGINATION.DEFAULT_ITEMS_PER_PAGE,
  }) {
    if (!query.transactionId && !query.listingId && !query.subjectId) {
      return Promise.reject('Expected at least one of the following properties: "transactionId", "listingId", "subjectId"')
    }

    return this.marketplace.reviews
      .query({
        ...query,
        include: ['author, author.profileImage, subject, subject.profileImage'],
        ...this.marketplaceService.getImageVariants(),
        page,
        perPage,
      })
      .then(response => mapRelations(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }
}
